import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, Users, TrendingUp, TrendingDown, Briefcase, 
  ArrowLeft, RefreshCw, FileText, CheckCircle, Clock, 
  XCircle, UserCheck, Globe, Calendar, Download, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../utils/api';
import { getToken, getUser } from '../utils/auth';

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const token = getToken();
    const user = getUser();
    if (!token || !user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchAnalytics();
  }, [navigate]);

  const fetchAnalytics = async (customStartDate, customEndDate) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      const sDate = customStartDate !== undefined ? customStartDate : startDate;
      const eDate = customEndDate !== undefined ? customEndDate : endDate;
      if (sDate) params.append('start_date', sDate);
      if (eDate) params.append('end_date', eDate);
      
      const url = params.toString() ? `/admin/analytics?${params.toString()}` : '/admin/analytics';
      const response = await api.get(url);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = () => {
    fetchAnalytics(startDate, endDate);
  };

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    fetchAnalytics('', '');
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const url = params.toString() ? `/admin/analytics/export?${params.toString()}` : '/admin/analytics/export';
      const response = await api.get(url, { responseType: 'blob' });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'applications_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500';
      case 'reviewed': return 'bg-yellow-500';
      case 'contacted': return 'bg-purple-500';
      case 'hired': return 'bg-green-500';
      case 'rejected': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <FileText className="w-4 h-4" />;
      case 'reviewed': return <Clock className="w-4 h-4" />;
      case 'contacted': return <Users className="w-4 h-4" />;
      case 'hired': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-3 border-brand-blue border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-zinc-500">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  const { summary, status_breakdown, top_jobs, top_countries, applications_over_time, recent_applications } = analytics;
  
  // Find max value for chart scaling
  const maxCount = Math.max(...applications_over_time.map(d => d.count), 1);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-analytics-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard/admin')}
                className="text-zinc-600 hover:text-zinc-900"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-brand-blue" />
                  Application Analytics
                </h1>
                <p className="text-sm text-zinc-500">Track your recruitment performance</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => fetchAnalytics()} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button 
                onClick={handleExportCSV} 
                variant="outline" 
                size="sm" 
                disabled={exporting}
                className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </div>
          </div>
          
          {/* Date Range Filter */}
          <div className="mt-4 flex flex-wrap items-center gap-3 pt-3 border-t border-zinc-100">
            <div className="flex items-center gap-2 text-sm text-zinc-600">
              <Filter className="w-4 h-4" />
              <span className="font-medium">Date Range:</span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-9 w-36"
                placeholder="Start date"
                data-testid="start-date-input"
              />
              <span className="text-zinc-400">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-9 w-36"
                placeholder="End date"
                data-testid="end-date-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleDateFilter} 
                size="sm" 
                className="h-9 bg-brand-blue hover:bg-brand-blue/90"
                data-testid="apply-filter-btn"
              >
                Apply Filter
              </Button>
              {(startDate || endDate) && (
                <Button 
                  onClick={clearDateFilter} 
                  variant="ghost" 
                  size="sm" 
                  className="h-9 text-zinc-500"
                  data-testid="clear-filter-btn"
                >
                  Clear
                </Button>
              )}
            </div>
            {(startDate || endDate) && (
              <div className="flex items-center gap-1 bg-brand-blue/10 text-brand-blue text-xs font-medium px-2 py-1 rounded-full">
                <Calendar className="w-3 h-3" />
                {startDate && endDate ? `${startDate} → ${endDate}` : startDate ? `From ${startDate}` : `Until ${endDate}`}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Applications */}
          <div className="bg-white rounded-xl p-5 border border-zinc-100 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                summary.week_change >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {summary.week_change >= 0 ? '+' : ''}{summary.week_change}%
              </span>
            </div>
            <p className="text-2xl font-bold text-zinc-900">{summary.total_applications}</p>
            <p className="text-sm text-zinc-500">Total Applications</p>
          </div>

          {/* This Week */}
          <div className="bg-white rounded-xl p-5 border border-zinc-100 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-cyan-600" />
              </div>
              {summary.week_change >= 0 ? (
                <TrendingUp className="w-5 h-5 text-green-500" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-500" />
              )}
            </div>
            <p className="text-2xl font-bold text-zinc-900">{summary.this_week}</p>
            <p className="text-sm text-zinc-500">This Week</p>
          </div>

          {/* Conversion Rate */}
          <div className="bg-white rounded-xl p-5 border border-zinc-100 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-zinc-900">{summary.conversion_rate}%</p>
            <p className="text-sm text-zinc-500">Reviewed Rate</p>
          </div>

          {/* Hire Rate */}
          <div className="bg-white rounded-xl p-5 border border-zinc-100 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-zinc-900">{summary.hire_rate}%</p>
            <p className="text-sm text-zinc-500">Hire Rate</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Applications Over Time Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl p-6 border border-zinc-100 shadow-sm">
            <h3 className="font-semibold text-zinc-900 mb-4">Applications (Last 30 Days)</h3>
            <div className="h-48 flex items-end gap-1">
              {applications_over_time.map((day, index) => (
                <div 
                  key={day.date} 
                  className="flex-1 flex flex-col items-center group"
                >
                  <div 
                    className="w-full bg-brand-blue/80 hover:bg-brand-blue rounded-t transition-all relative"
                    style={{ 
                      height: `${(day.count / maxCount) * 160}px`,
                      minHeight: day.count > 0 ? '8px' : '2px'
                    }}
                  >
                    {day.count > 0 && (
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-zinc-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {day.count} apps
                      </div>
                    )}
                  </div>
                  {index % 5 === 0 && (
                    <span className="text-[10px] text-zinc-400 mt-1 -rotate-45 origin-top-left">
                      {formatDate(day.date)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Status Breakdown */}
          <div className="bg-white rounded-xl p-6 border border-zinc-100 shadow-sm">
            <h3 className="font-semibold text-zinc-900 mb-4">Status Breakdown</h3>
            <div className="space-y-3">
              {Object.entries(status_breakdown).map(([status, count]) => {
                const total = summary.total_applications || 1;
                const percentage = (count / total) * 100;
                return (
                  <div key={status}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-white ${getStatusColor(status)}`}>
                          {getStatusIcon(status)}
                        </span>
                        <span className="text-sm font-medium text-zinc-700 capitalize">{status}</span>
                      </div>
                      <span className="text-sm font-semibold text-zinc-900">{count}</span>
                    </div>
                    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${getStatusColor(status)} transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {/* Top Jobs */}
          <div className="bg-white rounded-xl p-6 border border-zinc-100 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="w-5 h-5 text-brand-blue" />
              <h3 className="font-semibold text-zinc-900">Top Jobs</h3>
            </div>
            {top_jobs.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-4">No data yet</p>
            ) : (
              <div className="space-y-3">
                {top_jobs.map((job, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-zinc-100 rounded-full flex items-center justify-center text-xs font-semibold text-zinc-600">
                        {index + 1}
                      </span>
                      <span className="text-sm text-zinc-700 truncate max-w-[180px]" title={job.name}>
                        {job.name}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-zinc-900 bg-zinc-100 px-2 py-0.5 rounded">
                      {job.count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top Countries */}
          <div className="bg-white rounded-xl p-6 border border-zinc-100 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-brand-blue" />
              <h3 className="font-semibold text-zinc-900">Top Locations</h3>
            </div>
            {top_countries.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-4">No data yet</p>
            ) : (
              <div className="space-y-3">
                {top_countries.map((country, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center text-xs font-semibold text-cyan-600">
                        {index + 1}
                      </span>
                      <span className="text-sm text-zinc-700 truncate max-w-[180px]" title={country.name}>
                        {country.name}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-zinc-900 bg-zinc-100 px-2 py-0.5 rounded">
                      {country.count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Applications */}
          <div className="bg-white rounded-xl p-6 border border-zinc-100 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-brand-blue" />
              <h3 className="font-semibold text-zinc-900">Recent Applications</h3>
            </div>
            {recent_applications.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-4">No applications yet</p>
            ) : (
              <div className="space-y-3">
                {recent_applications.map((app, index) => (
                  <div key={index} className="flex items-start gap-3 pb-3 border-b border-zinc-50 last:border-0 last:pb-0">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {app.full_name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-900 truncate">{app.full_name}</p>
                      <p className="text-xs text-zinc-500 truncate">{app.job_title || 'General Application'}</p>
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full capitalize ${
                      app.status === 'new' ? 'bg-blue-100 text-blue-700' :
                      app.status === 'reviewed' ? 'bg-yellow-100 text-yellow-700' :
                      app.status === 'contacted' ? 'bg-purple-100 text-purple-700' :
                      app.status === 'hired' ? 'bg-green-100 text-green-700' :
                      app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {app.status || 'new'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
